import React, { forwardRef, useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import CalendarIcon from 'icons/Calendar.icon';
import { Datepicker, Dropdown } from 'ncoded-component-library';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import {
  DatepickerProps,
  DatepickerRef,
} from 'ncoded-component-library/build/components/organisms/Datepicker/models';
import { FieldRenderProps } from 'react-final-form';
import { formatDate } from './utils';
import FieldWrapper from 'components/FieldWrapper';
import { useTranslation } from 'react-i18next';
import { FieldWrapperProps } from 'components/FieldWrapper/FieldWrapper.component';

import './DatepickerField.styles.scss';

const TRIGGER_ID_KEYWORD = 'dp-trigger';

type DatepickerFieldProps = FieldWrapperProps &
  DatepickerProps &
  FieldRenderProps<string, HTMLElement> & {
    disabled?: boolean;
    customOnChange?: (date: Date, isValid: boolean) => void;
  };

const DatepickerField: React.ForwardRefRenderFunction<
  DatepickerRef,
  DatepickerFieldProps
> = (props, ref) => {
  const {
    className,
    input: { value, onChange: fOnChange, name },
    meta: { touched, error },
    disabled,
    customOnChange,
    ...rest
  } = props;
  const [dropdown, dropdownRef] = useCallbackRef<DropdownRef>();

  const hasError = useMemo(() => touched && error, [error, touched]);

  const { t } = useTranslation();

  const handleChange = useCallback(
    (date: Date) => {
      fOnChange(date);
      customOnChange?.(date, !hasError);
      dropdown.setIsOpen(false);
    },
    [hasError, dropdown, customOnChange, fOnChange],
  );

  useEffect(() => {
    disabled && fOnChange(undefined);
  }, [disabled, fOnChange]);

  const classes = classNames('datepicker-field', className);

  useEffect(() => {
    const clickcb = (ev: any) => {
      const triggerId = (ev as any).target.id;

      if (
        triggerId &&
        triggerId.includes(TRIGGER_ID_KEYWORD) &&
        triggerId !== `${name}-${TRIGGER_ID_KEYWORD}`
      )
        dropdown.setIsOpen(false);
    };
    document.addEventListener('click', clickcb);
    return () => {
      document.removeEventListener('click', clickcb);
    };
  }, [dropdown, name]);

  return (
    <FieldWrapper
      className={classes}
      error={error}
      hasError={hasError}
      name={name}
      {...rest}
    >
      <Dropdown
        className={classNames('datepicker-field__dropdown', {
          'datepicker-field__dropdown--error': hasError,
        })}
        ref={dropdownRef}
        trigger={
          <button
            type="button"
            className="datepicker-field__button"
            disabled={disabled}
            id={`${name}-${TRIGGER_ID_KEYWORD}`}
          >
            {value ? formatDate(value) : <p>{t('selectDate')}</p>}
            <CalendarIcon />
          </button>
        }
      >
        <Datepicker
          value={value ? new Date(value) : new Date()}
          onChange={handleChange}
          {...rest}
          ref={ref}
        />
      </Dropdown>
    </FieldWrapper>
  );
};
export default forwardRef(DatepickerField);
